import { parseApiError } from './error'

// Orval-generated URLs already include the /api prefix (e.g. /api/auth/login).
// API_BASE must be empty by default so we don't produce double-prefixed paths like
// /api/api/auth/login.  Set VITE_API_BASE_URL only when the backend is on a
// different origin (e.g. VITE_API_BASE_URL=https://api.example.com).
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[2] ?? '') : null
}

/**
 * Orval mutator. Wraps fetch with:
 *  - CSRF cookie → X-CSRFToken header
 *  - credentials: 'include' for Django session
 *  - 401 → redirect to /login (preserving original location for redirect param)
 *  - non-2xx → typed ApiError parsed from { errors: ... } response body
 *
 * Generated endpoints rely on this exact name + path (orval.config.ts).
 */
export async function customFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const csrf = getCookie('csrftoken')
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`

  // A multipart body must set its own Content-Type: the browser appends the
  // boundary token, and naming the type ourselves would strip it and leave the
  // server unable to split the parts.
  const isMultipart = options.body instanceof FormData

  const response = await fetch(fullUrl, {
    ...options,
    credentials: 'include',
    headers: {
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      Accept: 'application/json',
      ...(csrf && { 'X-CSRFToken': csrf }),
      ...options.headers,
    },
  })

  if (response.status === 401) {
    // safeRedirect-validated relative path (T1.7b implements safeRedirect; here we build relative-only)
    const here = window.location.pathname + window.location.search
    const redirect = encodeURIComponent(here)
    window.location.href = `/login?redirect=${redirect}`
    throw new Error('Unauthenticated, redirecting to login')
  }

  if (response.status === 204) {
    return undefined as T
  }

  const body = await response.json().catch(() => null)

  if (!response.ok) {
    throw parseApiError(response, body)
  }

  return body as T
}
